import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Filter, X, RotateCcw } from 'lucide-react';
import { SearchFilters } from '@/utils/optimizedQueries';

interface FilterDrawerProps {
  filters: SearchFilters;
  onFiltersChange: (filters: Partial<SearchFilters>) => void;
  onRemoveFilter: (filterKey: keyof SearchFilters) => void;
  onClearAll: () => void;
  activeFilters: Array<{ key: keyof SearchFilters; label: string; value: any }>;
  trigger?: React.ReactNode;
}

export const FilterDrawer = ({ 
  filters, 
  onFiltersChange, 
  onRemoveFilter, 
  onClearAll, 
  activeFilters,
  trigger
}: FilterDrawerProps) => {
  const [salaryRange, setSalaryRange] = useState([filters.salary_min || 0]);
  const [isOpen, setIsOpen] = useState(false);
  
  const handleSalaryChange = (value: number[]) => {
    setSalaryRange(value);
    onFiltersChange({ salary_min: value[0] > 0 ? value[0] : undefined });
  };

  const jobTypes = [
    { value: 'full-time', label: 'Full Time' },
    { value: 'part-time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'internship', label: 'Internship' }
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level' },
    { value: 'mid', label: 'Mid Level' },
    { value: 'senior', label: 'Senior Level' },
    { value: 'lead', label: 'Lead/Manager' },
    { value: 'executive', label: 'Executive' }
  ];

  const postedDates = [
    { value: '1', label: 'Last 24 hours' },
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: 'all', label: 'All time' }
  ];

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Filter className="w-4 h-4" />
            Filters
            {activeFilters.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 text-xs">
                {activeFilters.length}
              </Badge>
            )}
          </Button>
        )}
      </SheetTrigger>
      
      <SheetContent side="left" className="w-80 overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-center justify-between">
            <SheetTitle>Filters</SheetTitle>
            {activeFilters.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onClearAll}
                className="text-xs h-8 px-2"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Clear All
              </Button>
            )}
          </div>
          
          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {activeFilters.map((filter) => (
                <Badge 
                  key={filter.key} 
                  variant="secondary" 
                  className="text-xs cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors"
                  onClick={() => onRemoveFilter(filter.key)}
                >
                  {filter.label}
                  <X className="h-3 w-3 ml-1" />
                </Badge>
              ))}
            </div>
          )}
        </SheetHeader>

        <div className="space-y-6 pb-6">
          {/* Salary Range */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Salary Range (LKR)</Label>
            <div className="px-2">
              <Slider
                value={salaryRange}
                onValueChange={handleSalaryChange}
                max={500000}
                min={0}
                step={10000}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>0</span>
                <span className="font-medium">
                  {salaryRange[0] > 0 ? `${salaryRange[0].toLocaleString()}+` : 'Any'}
                </span>
                <span>500,000+</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Job Type */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Job Type</Label>
            <Select 
              value={filters.job_type || 'all'} 
              onValueChange={(value) => onFiltersChange({ 
                job_type: value === 'all' ? undefined : value 
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select job type" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Types</SelectItem>
                {jobTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Experience Level */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Experience Level</Label>
            <Select 
              value={filters.experience_level || 'all'} 
              onValueChange={(value) => onFiltersChange({ 
                experience_level: value === 'all' ? undefined : value 
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select experience" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Levels</SelectItem>
                {experienceLevels.map((level) => (
                  <SelectItem key={level.value} value={level.value}>
                    {level.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Work Type */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Work Arrangement</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="remote-drawer"
                  checked={filters.is_remote || false}
                  onCheckedChange={(checked) => onFiltersChange({ 
                    is_remote: checked as boolean 
                  })}
                />
                <Label htmlFor="remote-drawer" className="text-sm">Remote Work</Label>
              </div>
            </div>
          </div>

          {/* Posted Date */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Posted Date</Label>
            <Select 
              value="all" 
              onValueChange={() => {}}
            >
              <SelectTrigger>
                <SelectValue placeholder="Any time" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {postedDates.map((date) => (
                  <SelectItem key={date.value} value={date.value}>
                    {date.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <Button 
            onClick={() => setIsOpen(false)}
            className="w-full"
          >
            Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};